import { FLOW } from 'app/model/adapters/dataflow/IDataflow';
import { Metamodel } from 'app/model/base/model';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { DataFlowSchemaAdapterUtils } from './dataflow-schema-adapter-utils';
import { IDslScriptable } from 'app/model/adapters/dataflow/transformations/base-transformation.adapter';
import { DataFlowAdapterUtils } from './adapter.util';
import { DataFlowLoggingService } from 'app/authoring/services/dataflow-logger.service';
import { SourceAdapter } from './source.adapter';
import { InputTransformation } from 'app/model/dataflow/inputtransformation';
import { InputConfig } from 'app/model/dataflow/input-config';
import {
    BinaryDataFlowType,
    DateDataFlowType,
    TimeStampDataFlowType,
    ArrayDataFlowType,
    ByteDataFlowType,
    NumberDataFlowType,
    IDataFlowDataType,
    BooleanDataFlowType
} from 'app/model/dataflow/dataflow-datatype';
import { ComplexDataFlowType } from 'app/model/dataflow/dataflow-complex-datatype';
import { DataFlowExpressionUtils } from './expression.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(InputTransformation),
    asynchronous: true
})
export class InputAdapter extends SourceAdapter implements IDslScriptable {
    public async getDslScript(model: InputTransformation): Promise<string> {
        const props: string[] = [DataFlowSchemaAdapterUtils.getOutputDefinitionDsl(model), `order: ${model.order}`];
        await DataFlowLoggingService.logColumnInfos(
            this.context.contextData,
            model.columns,
            model.name,
            Metamodel.modelFor(InputTransformation).name,
            model.owner.name
        );

        props.push(
            `allowSchemaDrift: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                model.allowSchemaDrift,
                BooleanDataFlowType.TYPE
            )}`
        );

        const knownProps = props.filter((prop) => !!prop).join(',\n\t');
        const unknownProps = DataFlowAdapterUtils.getUnknownProps(model, knownProps);
        const transformationData = [knownProps, unknownProps].filter((c) => !!c).join(',\n\t');
        const outputStream = model.outputStream;
        return `input(${transformationData}) ${FLOW} ${outputStream ? outputStream.qualifiedName : ''}`;
    }

    public getMemoryStoreDSL(model: InputTransformation): string {
        const output = DataFlowSchemaAdapterUtils.getOutputDefinitionDsl(model);
        const data = `data: ${this.generateDataDSL(model)}`;
        const store = `store: 'memory'`;
        const format = `format: 'inline'`;
        const transformationData = [output, data, store, format].join(',\n\t');
        return `source(${transformationData}) ${FLOW} ${model.name}`;
    }

    private generateDataDSL(model: InputTransformation): string {
        return (
            '[' +
            model.debugData
                .map((inputConfig: InputConfig) => {
                    return (
                        '[' +
                        inputConfig.data
                            .map((item, index) => {
                                try {
                                    return this.convertType(model.schema[index].dataFlowColumnType, item);
                                } catch {
                                    return item;
                                }
                            })
                            .join(', ') +
                        ']'
                    );
                })
                .join(', ') +
            ']'
        );
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private convertType(columnType: IDataFlowDataType, value: string): any {
        if (
            columnType instanceof NumberDataFlowType ||
            columnType instanceof ByteDataFlowType ||
            columnType instanceof BinaryDataFlowType
        ) {
            return `'${Number(value)}'`;
        } else if (columnType instanceof DateDataFlowType) {
            return `'${new Date(value).toISOString().slice(0, 10)}'`;
        } else if (columnType instanceof TimeStampDataFlowType) {
            return `'${new Date(value).toISOString().split('T').join(' ').slice(0, 19)}'`;
        } else if (columnType instanceof ArrayDataFlowType) {
            value = value.replace(/'/g, '"');
            value = JSON.parse(value);
            return `'${value}'`;
        } else if (columnType instanceof BooleanDataFlowType) {
            return `'${value === 'true'}'`;
        } else if (columnType instanceof ComplexDataFlowType) {
            return `${value}`;
        } else {
            return `'${value}'`;
        }
    }
}
